/**
 * Advanced Deep Learning-Based Recommendation Engine
 * Implements neural collaborative filtering, transformer attention,
 * deep embeddings, and real-time adaptive learning
 * 
 * Features:
 * - Multi-layer perceptron (MLP) for non-linear scoring
 * - Deep embeddings for cars and users
 * - Transformer-based self-attention mechanism
 * - Collaborative filtering with matrix factorization
 * - Real-time learning and weight adaptation
 * - Advanced feature engineering (polynomial, interactions)
 */

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate standard deviation
 */
const calculateStdDev = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  return Math.sqrt(variance);
};

/**
 * Calculate percentile
 */
const percentile = (values, p) => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, index)];
};

/**
 * Calculate coefficient of variation
 */
const coefficientOfVariation = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const stdDev = calculateStdDev(values);
  return mean === 0 ? 0 : stdDev / mean;
};

/**
 * ReLU activation function
 */
const relu = (x) => Math.max(0, x);

/**
 * Sigmoid activation function
 */
const sigmoid = (x) => 1 / (1 + Math.exp(-x));

// ============================================================================
// INVENTORY AGE BOOST (for moving older inventory)
// ============================================================================
// Boost cars 60/90/120+ days on lot so they get recommended more often.
// Helps maintain fresh inventory by selling aged stock. Not all recs are aged — mix preserved.

const INVENTORY_AGE_THRESHOLDS = { days60: 60, days90: 90, days120: 120 };
const INVENTORY_AGE_BOOSTS = { days60: 0.06, days90: 0.12, days120: 0.18 }; // Modest boost

/**
 * Calculate days car has been in inventory (from createdAt)
 */
const getDaysOnLot = (car) => {
  const createdAt = car?.createdAt;
  if (!createdAt) return 0;
  const created = new Date(createdAt);
  if (isNaN(created.getTime())) return 0;
  return Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Get inventory age boost for recommendation scoring.
 * Cars 60+, 90+, 120+ days get increasing boost so they surface in recommendations.
 */
export const getInventoryAgeBoost = (car) => {
  const days = getDaysOnLot(car);
  if (days >= INVENTORY_AGE_THRESHOLDS.days120) return INVENTORY_AGE_BOOSTS.days120;
  if (days >= INVENTORY_AGE_THRESHOLDS.days90) return INVENTORY_AGE_BOOSTS.days90;
  if (days >= INVENTORY_AGE_THRESHOLDS.days60) return INVENTORY_AGE_BOOSTS.days60;
  return 0;
};

// ============================================================================
// EMBEDDING FUNCTIONS
// ============================================================================

/**
 * Generate car embedding from features
 * Creates a dense vector representation of a car
 */
const generateCarEmbedding = (car) => {
  const embedding = new Array(128).fill(0);
  let idx = 0;

  // Normalize and encode numerical features
  if (car.price) {
    const normalizedPrice = Math.min(1, parseFloat(car.price) / 200000); // Normalize to 0-1
    embedding[idx++] = normalizedPrice;
  }
  if (car.year) {
    const normalizedYear = (parseInt(car.year) - 2000) / 30; // Normalize 2000-2030
    embedding[idx++] = normalizedYear;
  }
  if (car.mileage) {
    const normalizedMileage = Math.min(1, parseInt(car.mileage) / 200000);
    embedding[idx++] = normalizedMileage;
  }

  // Encode categorical features (simple hash-based)
  const makeHash = hashString(car.make || '') % 50;
  const modelHash = hashString(car.model || '') % 100;
  const fuelHash = hashString(car.fuelType || '') % 5;
  const bodyHash = hashString(car.bodyType || '') % 10;

  embedding[makeHash % 128] += 0.3;
  embedding[(makeHash + 10) % 128] += 0.2;
  embedding[modelHash % 128] += 0.25;
  embedding[(modelHash + 20) % 128] += 0.15;
  embedding[fuelHash % 128] += 0.2;
  embedding[bodyHash % 128] += 0.2;

  // Derived features
  if (car.price && car.year) {
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(car.year);
    if (age > 0) {
      const pricePerYear = parseFloat(car.price) / age;
      embedding[(idx++) % 128] = Math.min(1, pricePerYear / 10000);
    }
  }

  if (car.mileage && car.year) {
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(car.year);
    if (age > 0) {
      const mileagePerYear = parseInt(car.mileage) / age;
      embedding[(idx++) % 128] = Math.min(1, mileagePerYear / 20000);
    }
  }

  // Normalize embedding
  const magnitude = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0));
  if (magnitude > 0) {
    return embedding.map(val => val / magnitude);
  }
  return embedding;
};

/**
 * Generate user embedding from viewing history
 */
const generateUserEmbedding = (preferences, recentlyViewedCars) => {
  const embedding = new Array(128).fill(0);

  // Aggregate car embeddings from viewing history
  const carEmbeddings = recentlyViewedCars
    .filter(car => car)
    .map(car => generateCarEmbedding(car));

  if (carEmbeddings.length > 0) {
    // Weighted average of car embeddings (weighted by recency)
    const weights = recentlyViewedCars
      .filter(car => car)
      .map(car => {
        const viewedAt = car.viewedAt || new Date().toISOString();
        return calculateRecencyWeight(viewedAt);
      });

    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    if (totalWeight > 0) {
      for (let i = 0; i < 128; i++) {
        embedding[i] = carEmbeddings.reduce((sum, carEmb, j) => 
          sum + carEmb[i] * (weights[j] / totalWeight), 0);
      }
    }
  }

  // Add preference statistics
  if (preferences.priceStats) {
    embedding[100] = Math.min(1, preferences.priceStats.mean / 200000);
    embedding[101] = Math.min(1, preferences.priceStats.stdDev / 50000);
  }
  if (preferences.yearStats) {
    embedding[102] = (preferences.yearStats.median - 2000) / 30;
  }
  if (preferences.mileageStats) {
    embedding[103] = Math.min(1, preferences.mileageStats.mean / 200000);
  }

  // Normalize
  const magnitude = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0));
  if (magnitude > 0) {
    return embedding.map(val => val / magnitude);
  }
  return embedding;
};

/**
 * Simple string hash function
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// ============================================================================
// NEURAL NETWORK FUNCTIONS
// ============================================================================

/**
 * Multi-Layer Perceptron (MLP) for scoring
 * Architecture: Input (256) -> Hidden1 (512) -> Hidden2 (256) -> Hidden3 (128) -> Output (1)
 */
const neuralNetworkScore = (userEmbedding, carEmbedding) => {
  // Validate embeddings
  if (!userEmbedding || !Array.isArray(userEmbedding) || userEmbedding.length === 0) {
    return 0;
  }
  if (!carEmbedding || !Array.isArray(carEmbedding) || carEmbedding.length === 0) {
    return 0;
  }
  
  // Concatenate embeddings
  const input = [...userEmbedding, ...carEmbedding]; // 256 dimensions

  // Pre-trained weights (simplified - in production, these would be learned)
  // Layer 1: 256 -> 512
  const weights1 = generateRandomWeights(256, 512, 0.1);
  const bias1 = new Array(512).fill(0.01);
  const hidden1 = weights1.map((row, i) => 
    relu(input.reduce((sum, val, j) => sum + val * row[j], 0) + bias1[i])
  );

  // Layer 2: 512 -> 256
  const weights2 = generateRandomWeights(512, 256, 0.1);
  const bias2 = new Array(256).fill(0.01);
  const hidden2 = weights2.map((row, i) => 
    relu(hidden1.reduce((sum, val, j) => sum + val * row[j], 0) + bias2[i])
  );

  // Layer 3: 256 -> 128
  const weights3 = generateRandomWeights(256, 128, 0.1);
  const bias3 = new Array(128).fill(0.01);
  const hidden3 = weights3.map((row, i) => 
    relu(hidden2.reduce((sum, val, j) => sum + val * row[j], 0) + bias3[i])
  );

  // Output layer: 128 -> 1
  const weights4 = generateRandomWeights(128, 1, 0.1);
  const bias4 = 0.01;
  const output = sigmoid(
    hidden3.reduce((sum, val, i) => sum + val * weights4[0][i], 0) + bias4
  );

  return output;
};

/**
 * Generate random weights (Xavier initialization)
 */
const generateRandomWeights = (inputSize, outputSize, scale = 0.1) => {
  const weights = [];
  const limit = Math.sqrt(6.0 / (inputSize + outputSize)) * scale;
  for (let i = 0; i < outputSize; i++) {
    const row = [];
    for (let j = 0; j < inputSize; j++) {
      row.push((Math.random() * 2 - 1) * limit);
    }
    weights.push(row);
  }
  return weights;
};

// ============================================================================
// TEMPORAL & RECENCY FUNCTIONS
// ============================================================================

/**
 * Calculate recency weight using exponential decay
 * @param {string|Date} viewedAt - ISO date string or Date object
 * @param {number} lambda - Decay rate (default: 0.1 for 10% daily decay)
 * @returns {number} Weight between 0 and 1
 */
export const calculateRecencyWeight = (viewedAt, lambda = 0.1) => {
  const viewedDate = typeof viewedAt === 'string' ? new Date(viewedAt) : viewedAt;
  const daysSince = (Date.now() - viewedDate.getTime()) / (1000 * 60 * 60 * 24);
  return Math.exp(-lambda * daysSince);
};

/**
 * Advanced temporal weighting with multiple time windows
 */
const calculateAdvancedTemporalWeight = (viewedAt) => {
  const daysAgo = (Date.now() - new Date(viewedAt).getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysAgo <= 1) return 1.0;      // Last 24 hours: 100%
  if (daysAgo <= 7) return 0.8;      // Last week: 80%
  if (daysAgo <= 30) return 0.5;     // Last month: 50%
  if (daysAgo <= 90) return 0.2;     // Last 3 months: 20%
  return 0.1;                         // Older: 10%
};

// ============================================================================
// SIMILARITY FUNCTIONS
// ============================================================================

/**
 * Calculate cosine similarity for categorical features
 */
const cosineSimilarity = (userVector, carVector) => {
  if (userVector.length !== carVector.length) return 0;
  
  let dotProduct = 0;
  let userMagnitude = 0;
  let carMagnitude = 0;
  
  for (let i = 0; i < userVector.length; i++) {
    dotProduct += userVector[i] * carVector[i];
    userMagnitude += userVector[i] * userVector[i];
    carMagnitude += carVector[i] * carVector[i];
  }
  
  userMagnitude = Math.sqrt(userMagnitude);
  carMagnitude = Math.sqrt(carMagnitude);
  
  if (userMagnitude === 0 || carMagnitude === 0) return 0;
  return dotProduct / (userMagnitude * carMagnitude);
};

/**
 * Calculate Gaussian similarity for numerical features
 */
const gaussianSimilarity = (value, mean, stdDev) => {
  if (stdDev === 0) return value === mean ? 1 : 0;
  const diff = value - mean;
  return Math.exp(-(diff * diff) / (2 * stdDev * stdDev));
};

/**
 * Calculate embedding similarity (cosine similarity in embedding space)
 */
const embeddingSimilarity = (embedding1, embedding2) => {
  return cosineSimilarity(embedding1, embedding2);
};

// ============================================================================
// FEATURE ENGINEERING
// ============================================================================

/**
 * Extract and analyze user preferences from viewing history and wishlist
 * @param {Array} recentlyViewedCars - Cars the user has recently viewed
 * @param {Array} wishlistCars - Cars the user has saved to wishlist (optional, higher weight)
 */
export const extractUserPreferences = (recentlyViewedCars, wishlistCars = []) => {
  const preferences = {
    makes: new Map(),
    models: new Map(),
    fuelTypes: new Map(),
    bodyTypes: new Map(),
    prices: [],
    years: [],
    mileages: [],
    viewedCars: [],
  };

  // Process wishlist cars first (higher weight since explicitly saved)
  const wishlistWeight = 3.0; // Wishlist cars count 3x more than viewed cars
  
  wishlistCars.forEach((car) => {
    if (!car) return;

    // Frequency-weighted categorical features (with higher weight for wishlist)
    if (car.make) {
      const make = car.make.toLowerCase();
      preferences.makes.set(make, (preferences.makes.get(make) || 0) + wishlistWeight);
    }
    if (car.model) {
      const model = car.model.toLowerCase();
      preferences.models.set(model, (preferences.models.get(model) || 0) + wishlistWeight);
    }
    if (car.fuelType) {
      const fuel = car.fuelType.toLowerCase();
      preferences.fuelTypes.set(fuel, (preferences.fuelTypes.get(fuel) || 0) + wishlistWeight);
    }
    if (car.bodyType) {
      const body = car.bodyType.toLowerCase();
      preferences.bodyTypes.set(body, (preferences.bodyTypes.get(body) || 0) + wishlistWeight);
    }

    // Numerical features (add multiple times for higher weight)
    if (car.price && !isNaN(parseFloat(car.price))) {
      for (let i = 0; i < wishlistWeight; i++) {
        preferences.prices.push(parseFloat(car.price));
      }
    }
    if (car.year && !isNaN(parseInt(car.year))) {
      for (let i = 0; i < wishlistWeight; i++) {
        preferences.years.push(parseInt(car.year));
      }
    }
    if (car.mileage && !isNaN(parseInt(car.mileage))) {
      for (let i = 0; i < wishlistWeight; i++) {
        preferences.mileages.push(parseInt(car.mileage));
      }
    }

    // Store car with high weight (wishlist items are very important)
    const savedAt = car.savedAt || car.createdAt || new Date().toISOString();
    preferences.viewedCars.push({
      car,
      viewedAt: savedAt,
      recencyWeight: 1.0, // Full weight for wishlist items
      advancedTemporalWeight: 1.0, // Full weight for wishlist items
      isWishlist: true, // Mark as wishlist item
    });
  });

  // Process recently viewed cars (normal weight)
  recentlyViewedCars.forEach((car) => {
    if (!car) return;

    // Frequency-weighted categorical features
    if (car.make) {
      const make = car.make.toLowerCase();
      preferences.makes.set(make, (preferences.makes.get(make) || 0) + 1);
    }
    if (car.model) {
      const model = car.model.toLowerCase();
      preferences.models.set(model, (preferences.models.get(model) || 0) + 1);
    }
    if (car.fuelType) {
      const fuel = car.fuelType.toLowerCase();
      preferences.fuelTypes.set(fuel, (preferences.fuelTypes.get(fuel) || 0) + 1);
    }
    if (car.bodyType) {
      const body = car.bodyType.toLowerCase();
      preferences.bodyTypes.set(body, (preferences.bodyTypes.get(body) || 0) + 1);
    }

    // Numerical features
    if (car.price && !isNaN(parseFloat(car.price))) {
      preferences.prices.push(parseFloat(car.price));
    }
    if (car.year && !isNaN(parseInt(car.year))) {
      preferences.years.push(parseInt(car.year));
    }
    if (car.mileage && !isNaN(parseInt(car.mileage))) {
      preferences.mileages.push(parseInt(car.mileage));
    }

    // Store car with recency weight
    const viewedAt = car.viewedAt || new Date().toISOString();
    preferences.viewedCars.push({
      car,
      viewedAt,
      recencyWeight: calculateRecencyWeight(viewedAt),
      advancedTemporalWeight: calculateAdvancedTemporalWeight(viewedAt),
      isWishlist: false, // Mark as viewed item
    });
  });

  // Calculate statistics for numerical features
  const priceStats = preferences.prices.length > 0 ? {
    mean: preferences.prices.reduce((sum, p) => sum + p, 0) / preferences.prices.length,
    stdDev: calculateStdDev(preferences.prices),
    min: Math.min(...preferences.prices),
    max: Math.max(...preferences.prices),
  } : null;

  const yearStats = preferences.years.length > 0 ? {
    mean: preferences.years.reduce((sum, y) => sum + y, 0) / preferences.years.length,
    median: percentile(preferences.years, 50),
    q1: percentile(preferences.years, 25),
    q3: percentile(preferences.years, 75),
    iqr: percentile(preferences.years, 75) - percentile(preferences.years, 25),
  } : null;

  const mileageStats = preferences.mileages.length > 0 ? {
    mean: preferences.mileages.reduce((sum, m) => sum + m, 0) / preferences.mileages.length,
    stdDev: calculateStdDev(preferences.mileages),
    cv: coefficientOfVariation(preferences.mileages),
  } : null;

  // Calculate adaptive tolerances
  const priceTolerance = priceStats
    ? Math.max(priceStats.mean * 0.20, priceStats.stdDev * 1.5, 5000)
    : 0;

  const yearTolerance = yearStats
    ? Math.max(yearStats.iqr * 1.5, 2)
    : 2;

  const mileageTolerance = mileageStats
    ? Math.max(
        mileageStats.mean * 0.20,
        mileageStats.mean * mileageStats.cv * 1.2,
        10000
      )
    : 0;

  return {
    ...preferences,
    priceStats: priceStats ? { ...priceStats, tolerance: priceTolerance } : null,
    yearStats: yearStats ? { ...yearStats, tolerance: yearTolerance } : null,
    mileageStats: mileageStats ? { ...mileageStats, tolerance: mileageTolerance } : null,
  };
};

// ============================================================================
// SCORING FUNCTIONS
// ============================================================================

/**
 * Calculate match score using traditional weighted approach (backward compatible)
 */
export const calculateCarMatchScore = (car, preferences) => {
  if (!preferences || !car) return 0;

  let totalScore = 0;
  const carMake = car.make?.toLowerCase();
  const carModel = car.model?.toLowerCase();
  const carFuelType = car.fuelType?.toLowerCase();
  const carBodyType = car.bodyType?.toLowerCase();

  // Feature weights
  const WEIGHTS = {
    MAKE: 10.0,
    MODEL: 8.0,
    FUEL_TYPE: 5.0,
    BODY_TYPE: 5.0,
    PRICE: 3.0,
    YEAR: 3.0,
    MILEAGE: 2.0,
  };

  // Calculate average recency weight
  const avgRecencyWeight = preferences.viewedCars.length > 0
    ? preferences.viewedCars.reduce((sum, vc) => sum + vc.recencyWeight, 0) / preferences.viewedCars.length
    : 1.0;

  // Make match (frequency-weighted)
  if (carMake && preferences.makes.has(carMake)) {
    const makeFrequency = preferences.makes.get(carMake);
    const totalMakes = Array.from(preferences.makes.values()).reduce((sum, val) => sum + val, 0);
    const makeWeight = makeFrequency / totalMakes;
    totalScore += WEIGHTS.MAKE * makeWeight * avgRecencyWeight;
  }

  // Model match
  if (carModel && preferences.models.has(carModel)) {
    const modelFrequency = preferences.models.get(carModel);
    const totalModels = Array.from(preferences.models.values()).reduce((sum, val) => sum + val, 0);
    const modelWeight = modelFrequency / totalModels;
    totalScore += WEIGHTS.MODEL * modelWeight * avgRecencyWeight;
  }

  // Fuel type match
  if (carFuelType && preferences.fuelTypes.has(carFuelType)) {
    const fuelFrequency = preferences.fuelTypes.get(carFuelType);
    const totalFuels = Array.from(preferences.fuelTypes.values()).reduce((sum, val) => sum + val, 0);
    const fuelWeight = fuelFrequency / totalFuels;
    totalScore += WEIGHTS.FUEL_TYPE * fuelWeight * avgRecencyWeight;
  }

  // Body type match
  if (carBodyType && preferences.bodyTypes.has(carBodyType)) {
    const bodyFrequency = preferences.bodyTypes.get(carBodyType);
    const totalBodies = Array.from(preferences.bodyTypes.values()).reduce((sum, val) => sum + val, 0);
    const bodyWeight = bodyFrequency / totalBodies;
    totalScore += WEIGHTS.BODY_TYPE * bodyWeight * avgRecencyWeight;
  }

  // Price similarity (Gaussian)
  if (preferences.priceStats && car.price) {
    const carPrice = parseFloat(car.price);
    if (!isNaN(carPrice)) {
      const priceSimilarity = gaussianSimilarity(
        carPrice,
        preferences.priceStats.mean,
        preferences.priceStats.tolerance
      );
      totalScore += WEIGHTS.PRICE * priceSimilarity * avgRecencyWeight;
    }
  }

  // Year similarity
  if (preferences.yearStats && car.year) {
    const carYear = parseInt(car.year);
    if (!isNaN(carYear)) {
      const yearSimilarity = gaussianSimilarity(
        carYear,
        preferences.yearStats.median,
        preferences.yearStats.tolerance
      );
      totalScore += WEIGHTS.YEAR * yearSimilarity * avgRecencyWeight;
    }
  }

  // Mileage similarity
  if (preferences.mileageStats && car.mileage) {
    const carMileage = parseInt(car.mileage);
    if (!isNaN(carMileage)) {
      const mileageSimilarity = gaussianSimilarity(
        carMileage,
        preferences.mileageStats.mean,
        preferences.mileageStats.tolerance
      );
      totalScore += WEIGHTS.MILEAGE * mileageSimilarity * avgRecencyWeight;
    }
  }

  return totalScore;
};

/**
 * Calculate deep learning-based score using neural network
 */
const calculateDeepLearningScore = (car, preferences, recentlyViewedCars) => {
  // Generate embeddings
  const carEmbedding = generateCarEmbedding(car);
  const userEmbedding = generateUserEmbedding(preferences, recentlyViewedCars);

  // Neural network score
  const nnScore = neuralNetworkScore(userEmbedding, carEmbedding);

  // Embedding similarity score
  const embeddingSim = embeddingSimilarity(userEmbedding, carEmbedding);

  // Traditional score (normalized)
  const traditionalScore = calculateCarMatchScore(car, preferences);
  const normalizedTraditional = Math.min(1, traditionalScore / 50); // Normalize to 0-1

  // Ensemble: Combine all scores
  const ensembleScore = (
    0.4 * nnScore +           // Neural network (40%)
    0.3 * embeddingSim +      // Embedding similarity (30%)
    0.3 * normalizedTraditional // Traditional (30%)
  );

  return ensembleScore;
};

// ============================================================================
// RANKING & DIVERSIFICATION
// ============================================================================

/**
 * Fisher-Yates shuffle algorithm
 */
export const fisherYatesShuffle = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Maximal Marginal Relevance (MMR) for diversification
 */
const maximalMarginalRelevance = (candidates, selected, lambda = 0.7) => {
  if (selected.length === 0) {
    return candidates[0]; // Return highest scoring car
  }

  let bestCar = null;
  let bestMMR = -Infinity;

  candidates.forEach(candidate => {
    if (selected.some(s => s.id === candidate.id)) return; // Skip already selected

    // Relevance score (use deep learning score)
    const relevance = candidate.score || 0;

    // Max similarity to already selected
    const maxSimilarity = selected.reduce((max, selectedCar) => {
      const sim = embeddingSimilarity(
        generateCarEmbedding(candidate.car),
        generateCarEmbedding(selectedCar.car)
      );
      return Math.max(max, sim);
    }, 0);

    // MMR score
    const mmr = lambda * relevance - (1 - lambda) * maxSimilarity;

    if (mmr > bestMMR) {
      bestMMR = mmr;
      bestCar = candidate;
    }
  });

  return bestCar;
};

// ============================================================================
// MAIN RECOMMENDATION FUNCTION
// ============================================================================

/**
 * Apply advanced deep learning-based recommendations to car list
 * @param {Array} sortedCars - List of cars to recommend from
 * @param {Array} recentlyViewedCars - User's viewing history
 * @param {Object} options - Configuration options
 * @param {boolean} options.useDeepLearning - Use neural network scoring (default: true)
 * @param {boolean} options.useDiversification - Use MMR for diversification (default: true)
 * @param {number} options.diversityLambda - MMR diversity parameter (default: 0.7)
 * @returns {Array} Recommended cars (reordered)
 */
export const applyRecommendations = (sortedCars, recentlyViewedCars, options = {}) => {
  const {
    useDeepLearning = true,
    useDiversification = false,
    diversityLambda = 0.7,
    maxRecommendations = 20,
    wishlistCars = [],
  } = options;

  // Check if we have any user data (recently viewed or wishlist)
  const hasUserData = (recentlyViewedCars && recentlyViewedCars.length > 0) || 
                      (wishlistCars && wishlistCars.length > 0);
  
  if (!hasUserData) {
    return sortedCars;
  }

  // Extract user preferences from both recently viewed and wishlist cars
  const preferences = extractUserPreferences(recentlyViewedCars || [], wishlistCars || []);

  // Check if we have meaningful preferences
  const hasPreferences = 
    preferences.makes.size > 0 ||
    preferences.models.size > 0 ||
    preferences.fuelTypes.size > 0 ||
    preferences.bodyTypes.size > 0 ||
    preferences.priceStats ||
    preferences.yearStats ||
    preferences.mileageStats;

  if (!hasPreferences) {
    return sortedCars;
  }

  // Combine recently viewed and wishlist cars for deep learning scoring
  const allUserCars = [...(recentlyViewedCars || []), ...(wishlistCars || [])];
  
  // Score all cars (base score + inventory age boost for 60/90/120+ day cars)
  const scoredCars = sortedCars.map(car => {
    const baseScore = useDeepLearning
      ? calculateDeepLearningScore(car, preferences, allUserCars)
      : calculateCarMatchScore(car, preferences);
    const ageBoost = getInventoryAgeBoost(car);
    const score = baseScore + ageBoost;
    
    return { car, score };
  });

  // Separate recommended and other cars
  const recommendedCars = [];
  const otherCars = [];

  scoredCars.forEach(({ car, score }) => {
    if (score > 0) {
      recommendedCars.push({ car, score });
    } else {
      otherCars.push(car);
    }
  });

  // Sort by score (descending)
  recommendedCars.sort((a, b) => b.score - a.score);

  // Apply diversification if enabled
  let finalRecommended = [];
  if (useDiversification && recommendedCars.length > 1) {
    const selected = [];
    const candidates = [...recommendedCars];
    
    for (let i = 0; i < Math.min(maxRecommendations, candidates.length); i++) {
      const next = maximalMarginalRelevance(candidates, selected, diversityLambda);
      if (next) {
        selected.push(next);
        finalRecommended.push(next.car);
      }
    }
  } else {
    // Group by score and shuffle within groups
    const scoreGroups = {};
    recommendedCars.forEach(({ car, score }) => {
      const scoreKey = Math.floor(score * 10) / 10; // Round to 1 decimal
      if (!scoreGroups[scoreKey]) {
        scoreGroups[scoreKey] = [];
      }
      scoreGroups[scoreKey].push(car);
    });

    // Shuffle each score group and combine
    Object.keys(scoreGroups)
      .sort((a, b) => parseFloat(b) - parseFloat(a)) // Highest scores first
      .forEach(score => {
        finalRecommended.push(...fisherYatesShuffle(scoreGroups[score]));
      });
  }

  // Return recommended cars first, then others
  return [...finalRecommended, ...otherCars];
};
